import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { string } from 'prop-types';
import { Onboarding } from '@templates';
import { Typography, Button, Flex } from '@mixins';
import { theme } from '@styles';
import { routes } from '@constants';
import { ONBOARDING_STEPS, ONBOARDING_STEPS_LABELING, ONBOARDING_ROUTES_BY_STEPS } from '@constants/common';
import { updateCompanyOnboarding } from '@store/company/duck';

import WelcomeOnboarding from '@assets/images/WelcomeOnboarding.png';

const StartOnboarding = ({ direction, location }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { onboardingState } = useSelector(store => store.companyStore);

  useEffect(() => {
    if (Object.keys(onboardingState).length && direction === 'forward' && !onboardingState?.isSubmitted) {
      history.push(ONBOARDING_ROUTES_BY_STEPS[onboardingState.currentStep], { direction: 'forward' });
    }
  }, [onboardingState]);

  const handleBack = () => {
    history.goBack();
  };

  const handleNavigateNext = () => {
    history.push(routes.teamOnboarding, { direction: 'forward' });
  };

  const handleNext = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.WELCOME,
        nextStep: ONBOARDING_STEPS.REGISTER_TEAM,
      }),
    );

    handleNavigateNext();
  };

  const availableOnboardingSteps = useMemo(() => {
    const stepsOrder = Object.values(ONBOARDING_STEPS);
    return (onboardingState?.availableSteps || [])
      .filter(step => step !== ONBOARDING_STEPS.WELCOME)
      .sort((a, b) => stepsOrder.indexOf(a) - stepsOrder.indexOf(b));
  }, [onboardingState?.availableSteps]);

  const splitIndex = Math.ceil(availableOnboardingSteps.length / 2);
  const firstColumnSteps = availableOnboardingSteps.slice(0, splitIndex);
  const secondColumnSteps = availableOnboardingSteps.slice(splitIndex);

  const onboardingSteps = useMemo(
    () => (
      <Flex display="grid" gridTemplateColumns="1fr 1fr" columnGap={[20, 40]} mb={10}>
        <Flex flexDirection="column">
          {firstColumnSteps.map(step => (
            <Flex key={step} mb={[12, 16]} alignItems="center">
              <Flex
                mr="10px"
                width="10px"
                height="10px"
                backgroundColor={theme.color.mintMinded[300]}
                borderRadius="50%"
              />
              <Typography variant="diatype-400" fontSize={[12, 14, 16, 18]} color={theme.color.mintMinded[900]}>
                {ONBOARDING_STEPS_LABELING[step]}
              </Typography>
            </Flex>
          ))}
        </Flex>

        <Flex flexDirection="column">
          {secondColumnSteps.map(step => (
            <Flex key={step} mb={[12, 16]} alignItems="center">
              <Flex
                mr="10px"
                width="10px"
                height="10px"
                backgroundColor={theme.color.mintMinded[300]}
                borderRadius="50%"
              />
              <Typography variant="diatype-400" fontSize={[12, 14, 16, 18]} color={theme.color.mintMinded[900]}>
                {ONBOARDING_STEPS_LABELING[step]}
              </Typography>
            </Flex>
          ))}
        </Flex>
      </Flex>
    ),
    [firstColumnSteps, secondColumnSteps],
  );

  return (
    <Onboarding
      onNext={handleNext}
      direction={direction}
      title={`Welcome to${location.length <= 20 ? `\n` : ' '}${location}!`}
      image={WelcomeOnboarding}
      showNavigationItems={false}
      onBack={handleBack}>
      <Typography variant="diatype-300" fontSize={[14, 16, 18, 20]} mb={[20, 24]} color={theme.color.mintMinded[900]}>
        We&apos;re so happy to have you here! Let&apos;s get you ready for your move-in. This will only take a few
        minutes. Here is what we will need to get:
      </Typography>
      {onboardingSteps}
      <Button variant="primary" py={[10, 14, 20]} width="100%" onClick={handleNext}>
        Let&apos;s Get Started
      </Button>
    </Onboarding>
  );
};

StartOnboarding.defaultProps = {
  location: '',
  direction: 'forward',
};

StartOnboarding.propTypes = {
  location: string,
  direction: string,
};

export default StartOnboarding;
